import React, { useEffect, useState } from "react";
import axiosClient from "../../axios";

const AffiliateActivationCodes = () => {
  const [batches, setBatches] = useState([]);
  const [codes, setCodes] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);

  const [stats, setStats] = useState({
    total: 0,
    used: 0,
    unused: 0,
  });

  const [loading, setLoading] = useState(true);
  const [codesLoading, setCodesLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    try {
      setLoading(true);

      const response = await axiosClient.get("/api/affiliate/codes/batches");

      if (response.data.success) {
        const batchData = response.data.batches || [];

        setBatches(batchData);

        let total = 0;
        let used = 0;
        let unused = 0;

        batchData.forEach((batch) => {
          total += Number(batch.total_codes);
          used += Number(batch.used_codes);
          unused += Number(batch.unused_codes);
        });

        setStats({
          total,
          used,
          unused,
        });
      }
    } catch (err) {
      setError(
        err.response?.data?.message || "Unable to load activation codes.",
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchCodes = async (batchName) => {
    try {
      setCodesLoading(true);
      setSelectedBatch(batchName);

      const response = await axiosClient.get(
        `/api/affiliate/codes/batch/${batchName}`,
      );

      if (response.data.success) {
        setCodes(response.data.codes || []);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Unable to load codes.");
    } finally {
      setCodesLoading(false);
    }
  };

  const downloadExcel = async (batchName) => {
    try {
      const response = await axiosClient.get(
        `/api/codes/batch/${batchName}/download`,
        {
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;
      link.download = `${batchName}-activation-codes.xlsx`;

      document.body.appendChild(link);

      link.click();

      link.remove();
    } catch (err) {
      alert("Failed to download Excel.");
    }
  };

  if (loading) {
    return (
      <div className="container py-4">
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="card shadow-sm border-0">
        <div className="card-header bg-white">
          <h3 className="mb-0">Activation Codes</h3>

          <small className="text-muted">
            View all activation codes assigned to your affiliate account.
          </small>
        </div>

        <div className="card-body">
          {error && <div className="alert alert-danger">{error}</div>}

          <div className="row mb-4">
            <div className="col-md-4 mb-3">
              <div className="card bg-primary text-white border-0">
                <div className="card-body">
                  <h6>Total Codes</h6>
                  <h2>{stats.total}</h2>
                </div>
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <div className="card bg-success text-white border-0">
                <div className="card-body">
                  <h6>Unused Codes</h6>
                  <h2>{stats.unused}</h2>
                </div>
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <div className="card bg-danger text-white border-0">
                <div className="card-body">
                  <h6>Used Codes</h6>
                  <h2>{stats.used}</h2>
                </div>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm">
            <div className="card-header">
              <strong>Activation Code Batches</strong>
            </div>

            <div className="card-body">
              {batches.length === 0 ? (
                <div className="alert alert-info mb-0">
                  No activation codes have been assigned yet.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle">
                    <thead>
                      <tr>
                        <th>Batch</th>
                        <th>Total</th>
                        <th>Used</th>
                        <th>Unused</th>
                        <th width="180">Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {batches.map((batch, index) => (
                        <tr key={index}>
                          <td>
                            <strong>{batch.batch_name}</strong>
                          </td>

                          <td>{batch.total_codes}</td>

                          <td>
                            <span className="badge bg-danger">
                              {batch.used_codes}
                            </span>
                          </td>

                          <td>
                            <span className="badge bg-success">
                              {batch.unused_codes}
                            </span>
                          </td>

                          <td>
                            <button
                              className="btn btn-primary btn-sm me-2"
                              onClick={() => fetchCodes(batch.batch_name)}
                            >
                              View
                            </button>

                            <button
                              className="btn btn-success btn-sm"
                              onClick={() => downloadExcel(batch.batch_name)}
                            >
                              Excel
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
          {selectedBatch && (
            <div className="card border-0 shadow-sm mt-4">
              <div className="card-header d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-0">{selectedBatch}</h5>
                  <small className="text-muted">
                    Activation codes in this batch
                  </small>
                </div>

                <button
                  className="btn btn-success btn-sm"
                  onClick={() => downloadExcel(selectedBatch)}
                >
                  Download Excel
                </button>
              </div>

              <div className="card-body">
                {codesLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" />
                  </div>
                ) : codes.length === 0 ? (
                  <div className="alert alert-info mb-0">
                    No activation codes found.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-bordered table-hover align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>Activation Code</th>
                          <th>Status</th>
                          <th>Used By</th>
                          <th>Used At</th>
                          <th>Expiry</th>
                        </tr>
                      </thead>

                      <tbody>
                        {codes.map((code) => (
                          <tr key={code.id}>
                            <td>
                              <strong>{code.code}</strong>
                            </td>

                            <td>
                              {code.is_used ? (
                                <span className="badge bg-danger">Used</span>
                              ) : (
                                <span className="badge bg-success">
                                  Available
                                </span>
                              )}
                            </td>

                            <td>{code.user?.name || "-"}</td>

                            <td>
                              {code.used_at
                                ? new Date(code.used_at).toLocaleString()
                                : "-"}
                            </td>

                            <td>
                              {code.expires_at
                                ? new Date(code.expires_at).toLocaleDateString()
                                : "No Expiry"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AffiliateActivationCodes;
